// src/components/PdamBillCalculator.jsx
import React, { useState, useEffect } from 'react';
import axios from 'axios';
import '../styles/PdamBillCalculator.css';

const API_BASE_URL = 'https://depotaircerdas.com/api/pdam';

const PdamBillCalculator = () => {
    const [pdamOptions, setPdamOptions] = useState([]);
    const [selectedPdam, setSelectedPdam] = useState('');
    const [golonganOptions, setGolonganOptions] = useState([]);
    const [selectedGolongan, setSelectedGolongan] = useState('');

    const [username, setUsername] = useState(localStorage.getItem('username') || '');

    const [selectedMonth, setSelectedMonth] = useState('');
    const [selectedYear, setSelectedYear] = useState('');
    const [usageM3, setUsageM3] = useState(null);
    const [billResult, setBillResult] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    const currentYear = new Date().getFullYear();
    const years = Array.from({ length: 5 }, (_, i) => currentYear - i);
    const months = [
        { value: '01', label: 'Januari' }, { value: '02', label: 'Februari' },
        { value: '03', label: 'Maret' }, { value: '04', label: 'April' },
        { value: '05', label: 'Mei' }, { value: '06', label: 'Juni' },
        { value: '07', label: 'Juli' }, { value: '08', label: 'Agustus' },
        { value: '09', label: 'September' }, { value: '10', label: 'Oktober' },
        { value: '11', label: 'November' }, { value: '12', label: 'Desember' },
    ];

    useEffect(() => {
        // Set default month and year to current
        const today = new Date();
        const currentMonthFormatted = String(today.getMonth() + 1).padStart(2, '0');
        const currentYearFormatted = String(today.getFullYear());
        setSelectedMonth(currentMonthFormatted);
        setSelectedYear(currentYearFormatted);

        const fetchInitialData = async () => {
            try {
                const token = localStorage.getItem('token');
                if (!token) {
                    setError('Autentikasi diperlukan. Silakan login kembali.');
                    setLoading(false);
                    return;
                }

                setLoading(true); // Mulai loading untuk keseluruhan proses
                // Ambil opsi PDAM
                const pdamResponse = await axios.get(`${API_BASE_URL}/pdam-options`, {
                    headers: { Authorization: `Bearer ${token}` }
                });

                const pdamDataArray = Object.entries(pdamResponse.data.data).map(([pdamId, pdamInfo]) => ({
                    pdamId: pdamId,
                    name: pdamInfo.name,
                    golongan: Object.entries(pdamInfo.golongan).map(([golonganId, description]) => ({
                        golonganId: golonganId,
                        description: description
                    }))
                }));

                setPdamOptions(pdamDataArray);

                if (pdamDataArray.length > 0) {
                    setSelectedPdam(pdamDataArray[0].pdamId);
                    const defaultPdam = pdamDataArray[0];
                    if (defaultPdam.golongan.length > 0) {
                        setGolonganOptions(defaultPdam.golongan);
                        setSelectedGolongan(defaultPdam.golongan[0].golonganId);
                    } else {
                        setGolonganOptions([]);
                        setSelectedGolongan('');
                    }
                } else {
                    setPdamOptions([]);
                    setSelectedPdam('');
                    setGolonganOptions([]);
                    setSelectedGolongan('');
                    setError('Tidak ada data PDAM tersedia dari server.');
                }
            } catch (err) {
                console.error('Error fetching initial data (PDAM options):', err);
                setError('Gagal memuat opsi PDAM. Pastikan backend berjalan dan token valid.');
            } finally {
                // Loading akan diatur setelah fetchUsageAutomatically selesai di efek selanjutnya
            }
        };
        fetchInitialData();
    }, []);


    useEffect(() => {
        const pdam = pdamOptions.find(p => p.pdamId === selectedPdam);
        if (pdam) {
            setGolonganOptions(pdam.golongan);
            if (pdam.golongan.length > 0) {
                setSelectedGolongan(pdam.golongan[0].golonganId);
            } else {
                setSelectedGolongan('');
            }
        }
    }, [selectedPdam, pdamOptions]);


    useEffect(() => {
        const fetchAndCalculateAutomatically = async () => {
            // Hanya panggil jika semua kriteria terpenuhi dan tidak sedang loading dari init
            if (!username || !selectedMonth || !selectedYear || !selectedPdam || !selectedGolongan || pdamOptions.length === 0) {
                // Jangan set error terlalu cepat jika hanya inisialisasi awal
                if (username && pdamOptions.length > 0) { // Set error jika ada yang tidak lengkap setelah init
                    setError('Harap lengkapi semua pilihan: Username, Bulan, Tahun, PDAM, dan Golongan.');
                }
                setUsageM3(null);
                setBillResult(null);
                setLoading(false); // Pastikan loading false jika tidak ada yang diproses
                return;
            }

            const token = localStorage.getItem('token');
            if (!token) {
                setError('Anda tidak terautentikasi. Silakan login kembali.');
                setLoading(false);
                return;
            }

            setLoading(true); // Mulai loading untuk keseluruhan proses
            setError(null);
            setUsageM3(null); // Clear previous usage
            setBillResult(null); // Clear previous bill result

            try {
                // --- LANGKAH 1: Ambil data penggunaan air ---
                let fetchedUsageM3 = null;
                try {
                    const usageResponse = await axios.get(`${API_BASE_URL}/total-usage/${username}?month=${selectedMonth}&year=${selectedYear}`, {
                        headers: { Authorization: `Bearer ${token}` }
                    });
                    
                    if (usageResponse.data && usageResponse.data.data && typeof usageResponse.data.data.total_m3 === 'number') {
                        fetchedUsageM3 = usageResponse.data.data.total_m3;
                        console.log(`Total penggunaan untuk ${username} di bulan ${selectedMonth}/${selectedYear}: ${fetchedUsageM3} m³`);
                        
                        if (fetchedUsageM3 === 0) {
                            setError('Tidak ada penggunaan air yang tercatat untuk periode ini. Pastikan username dan periode benar.');
                            setUsageM3(0); // Set ke 0 agar hasil perhitungan bisa ditampilkan untuk usage 0
                        }
                    } else {
                        setError('Format data penggunaan air tidak valid dari server atau data penggunaan kosong.');
                        setUsageM3(0); // Set ke 0 jika invalid format
                        setLoading(false);
                        return;
                    }
                } catch (usageErr) {
                    console.error('Error fetching usage:', usageErr.response ? usageErr.response.data : usageErr.message);
                    setError(`Gagal mendapatkan data penggunaan air: ${usageErr.response ? usageErr.response.data.message || usageErr.response.data.error : usageErr.message}`);
                    setUsageM3(0); // Set ke 0 jika ada error fetching usage
                    setLoading(false);
                    return;
                }

                // Set usageM3 setelah berhasil diambil (penting untuk UI)
                setUsageM3(fetchedUsageM3);

                // --- LANGKAH 2: Lanjutkan dengan perhitungan tagihan ---
                const billResponse = await axios.post(`${API_BASE_URL}/calculate-bill`, {
                    pdamId: selectedPdam,
                    golonganId: selectedGolongan,
                    usageM3: fetchedUsageM3 // Gunakan data usage yang baru diambil
                }, {
                    headers: { Authorization: `Bearer ${token}` }
                });

                if (billResponse.data && billResponse.data.data) {
                    setBillResult(billResponse.data.data);
                    console.log('Perhitungan tagihan berhasil!', billResponse.data.data);
                } else {
                    setError('Format data tagihan tidak valid dari server.');
                }
            } catch (err) {
                console.error('Error calculating bill (overall):', err.response ? err.response.data : err.message);
                setError(`Gagal menghitung tagihan: ${err.response ? err.response.data.message || err.response.data.error : err.message}`);
                setBillResult(null);
            } finally {
                setLoading(false); // Selesai loading setelah perhitungan bill
            }
        };

        // Panggil fungsi saat dependencies berubah
        fetchAndCalculateAutomatically();

    }, [username, selectedMonth, selectedYear, selectedPdam, selectedGolongan, pdamOptions.length]); // Tambahkan pdamOptions.length untuk memicu setelah opsi PDAM dimuat

    const formatRupiah = (amount) => {
        if (typeof amount !== 'number' || isNaN(amount)) return 'Rp 0';
        return new Intl.NumberFormat('id-ID', { style: 'currency', currency: 'IDR', minimumFractionDigits: 0 }).format(amount);
    };

    return (
        <div className="pdam-calculator-container">
            <h2>Perhitungan Tagihan PDAM</h2>
            
            {/* Disclaimer */}
            <p className="disclaimer">
                Disclaimer: Ini adalah perhitungan estimasi yang bisa saja salah dan didapatkan dari penggunaan air bulan ini,
                dan dikalikan dengan perhitungan PDAM pada masing-masing. Kami tidak bertanggung jawab atas kesalahan perhitungan,
                ini hanya estimasi yang mungkin saja mendekati.
            </p>

            {error && <div className="error-message">{error}</div>}
            {loading && <div className="loading-message">Memuat...</div>}

            <div className="calculator-layout-grid">
                {/* Kolom Kiri: Input Formulir */}
                <div className="input-column">
                    <div className="form-group">
                        <label htmlFor="username">Username Depot:</label>
                        <input
                            type="text"
                            id="username"
                            value={username}
                            onChange={(e) => setUsername(e.target.value)}
                            placeholder="Masukkan username depot"
                            disabled={loading}
                            readOnly // <-- DIBUAT READONLY
                            style={{ backgroundColor: '#f0f0f0', cursor: 'not-allowed' }}
                        />
                    </div>

                    <div className="form-group">
                        <label htmlFor="month">Bulan:</label>
                        <select id="month" value={selectedMonth} onChange={(e) => setSelectedMonth(e.target.value)} disabled={loading}>
                            {months.map(month => (
                                <option key={month.value} value={month.value}>{month.label}</option>
                            ))}
                        </select>
                    </div>

                    <div className="form-group">
                        <label htmlFor="year">Tahun:</label>
                        <select id="year" value={selectedYear} onChange={(e) => setSelectedYear(e.target.value)} disabled={loading}>
                            {years.map(year => (
                                <option key={year} value={year}>{year}</option>
                            ))}
                        </select>
                    </div>

                    {/* Bagian ini menampilkan penggunaan air setelah dihitung di kolom input */}
                    {usageM3 !== null && (
                        <div className="usage-display">
                            <h3>Penggunaan Air Bulan Ini: {usageM3.toFixed(2)} m³</h3>
                            <p>*Data ini diambil dari catatan penggunaan sensor Anda.</p>
                        </div>
                    )}

                    <div className="form-group">
                        <label htmlFor="pdam">Pilih PDAM:</label>
                        <select id="pdam" value={selectedPdam} onChange={(e) => setSelectedPdam(e.target.value)} disabled={loading || pdamOptions.length === 0}>
                            {pdamOptions.map(pdam => (
                                <option key={pdam.pdamId} value={pdam.pdamId}>{pdam.name}</option>
                            ))}
                        </select>
                    </div>

                    <div className="form-group">
                        <label htmlFor="golongan">Pilih Golongan Pelanggan:</label>
                        <select id="golongan" value={selectedGolongan} onChange={(e) => setSelectedGolongan(e.target.value)} disabled={loading || golonganOptions.length === 0}>
                            {golonganOptions.map(gol => (
                                <option key={gol.golonganId} value={gol.golonganId}>{gol.description}</option>
                            ))}
                        </select>
                    </div>
                </div>

                {/* Kolom Kanan: Hasil Perhitungan */}
                <div className="result-column">
                    {billResult && (
                        <div className="bill-result">
                            <h3>Hasil Perhitungan Tagihan</h3>
                            <p><strong>PDAM:</strong> {billResult.pdam_name}</p>
                            <p><strong>Golongan:</strong> {billResult.golongan}</p>
                            <p><strong>Penggunaan:</strong> {billResult.usage_m3.toFixed(2)} m³</p>
                            <p><strong>Total Tagihan:</strong> {formatRupiah(billResult.total_tagihan)}</p>
                            <h4>Detail Biaya:</h4>
                            <ul>
                                <li>Biaya Pemakaian Air: {formatRupiah(billResult.detail_biaya.biaya_pemakaian_air)}</li>
                                <li>Biaya Administrasi: {formatRupiah(billResult.detail_biaya.biaya_administrasi)}</li>
                                <li>Biaya Pemeliharaan Meter: {formatRupiah(billResult.detail_biaya.biaya_pemeliharaan_meter)}</li>
                                <li>Subtotal (sebelum PPN & Materai): {formatRupiah(billResult.detail_biaya.sub_total_sebelum_ppn_materai)}</li>
                                <li>PPN ({billResult.detail_biaya.ppn_persentase}%): {formatRupiah(billResult.detail_biaya.ppn_jumlah)}</li>
                                <li>Materai: {formatRupiah(billResult.detail_biaya.materai_jumlah)}</li>
                            </ul>
                        </div>
                    )}
                     {/* Pesan jika tidak ada hasil (misal loading awal, atau error 0 usage) */}
                     {!billResult && !loading && !error && usageM3 !== null && usageM3 === 0 && (
                        <div className="info-message">
                            Tidak ada tagihan yang dapat dihitung untuk penggunaan 0 m³.
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};

export default PdamBillCalculator;